/**
 * Convert dates passed in as strings from the Json API to localised values.
 *
 * The API will always pass ISO dates, so it _should_ be safe to use Date.parse()...
 *
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/DateTimeFormat
 *
 * Date/Time widgets
 * -----------------
 * Year (YYYY)
 * DateTimeStamp (HH:MMpm DD MMM YYYY)
 * DateStamp (DD MMM YYYY)
 * DateOnly (DD MMM)
 * TimeOnly (HH:MMpm)
 * 
 * ALSO SEE:
 * ./Sort.js 
 *   - dateToKey()
 *   - dateToHour()
 */
import spacetime from 'spacetime';
// import { dateToLocalISO } from './Sort';
import { intlPartsReducer } from './Sort';

/** Ensure the parameter is definitely a Date object
 * @param obj {String|Date} Possible date
 * @returns Date
 */
export const validateDateTime = (obj) => {
    if (!obj) { return null; }
    if (obj instanceof Date) { return obj; }

    const parsed = Date.parse(obj);
    if (isNaN(parsed)) {
        console.log("validateDateTime failed: ", obj)
    }
    return parsed;
};


export const asYear = (str) => {
    if (! str) { return '-'; }
    const parsed = Date.parse(str);
    if (isNaN(parsed)) {
        console.log("Date not recognised as valid:", str);
        return '?';
    }
    return parsed.getFullYear();
};
/*
export const asDateTimeStamp = (str) => {
    if (! str) { return '-'; }
    const parsed = Date.parse(str);
    if (isNaN(parsed)) {
        console.log("Date not recognised as valid:", str);
        return '?';
    }

    const options = {
        hour: 'numeric',
        minute: 'numeric',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    };
    return new Intl.DateTimeFormat(navigator.language, options).format(parsed);
};
*/
export const asDateStamp = (str, timeZone) => {
    if (! str) { return '-'; }
    const parsed = Date.parse(str);
    if (isNaN(parsed)) {
        console.log("Date not recognised as valid:", str);
        return '?';
    }

    const options = {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    };

    if (timeZone) {
        options['timeZone'] = timeZone;
    }
    return new Intl.DateTimeFormat(navigator.language, options).format(parsed);
};

var num_date_only = 0;
export const asDateOnly = (str, timeZone) => {
    if (! str) { return '-'; }
    const parsed = Date.parse(str);
    if (isNaN(parsed)) {
        console.log("Date not recognised as valid:", str);
        return '?';
    }

    const options = {
        month: 'short',
        day: 'numeric'
    };

    if (timeZone) {
        options['timeZone'] = timeZone;
    }
    num_date_only++;
    console.log('asDateOnly', num_date_only);
    return new Intl.DateTimeFormat(navigator.language, options).format(parsed);
};

/* Format a bare ISO Date string as "month day" in the current locale+timezone
 */
export const asLocalDateOnly = (dateStr) => {
    if (! dateStr) { return '-'; }
    const parsed = new Date(dateStr);
    // const parsed = Date.parse(dateStr);
    if (isNaN(parsed)) {
        console.log("Date not recognised as valid:", dateStr);
        return '?';
    }

    const options = {
        month: 'short',
        day: 'numeric'
    };
    // Adjust time to nullify the implicit tz offset
    parsed.setMinutes(parsed.getMinutes() + parsed.getTimezoneOffset())
    return parsed.toLocaleDateString(navigator.language, options)
    // return new Intl.DateTimeFormat(navigator.language, options).format(parsed);
};


// var num_time_only = 0;
export const asTimeOnly = (str, timeZone) => {
    if (! str) { return '-'; }
    const parsed = Date.parse(str);
    if (isNaN(parsed)) {
        console.log("Date not recognised as valid:", str);
        return '?';
    }

    const options = {
        hour: 'numeric',
        minute: 'numeric'
    };

    if (timeZone) {
        options['timeZone'] = timeZone;
    }
    // num_time_only++;
    // console.log('asTimeOnly', num_time_only);
    return new Intl.DateTimeFormat(navigator.language, options).format(parsed);
};

export const asDateTimeStamp = (str, timeZone) => {
    if (! str) { return '-'; }
    return asTimeOnly(str, timeZone)+' '+asDateStamp(str, timeZone);
};

export const asDayOfWeek = (str, timeZone) => {
    if (! str) { return '-'; }
    const parsed = Date.parse(str);
    if (isNaN(parsed)) {
        console.log("Date not recognised as valid:", str);
        return '?';
    }

    const options = {
        weekday: 'long'
    };

    if (timeZone) {
        options['timeZone'] = timeZone;
    }

    return new Intl.DateTimeFormat(navigator.language, options).format(parsed);
};


export const asTimeZoneAbbreviation = (str, timeZone) => {
    console.log('asTimeZoneAbbreviation:', str, timeZone);
    if (! str) { return '-'; }
    const parsed = Date.parse(str);
    if (isNaN(parsed)) {
        console.log("Date not recognised as valid:", str);
        return '?';
    }

    // let formatter = new Intl.DateTimeFormat('en-US', {
    let formatter = new Intl.DateTimeFormat(navigator.language, {
        day: '2-digit',
        timeZone: timeZone,
        timeZoneName: 'short',
        // timeZoneName: 'long',
    });
    // num_datetokey++;
    // console.log("dateToKey", num_datetokey);
    let parts = formatter.formatToParts(parsed).reduce(intlPartsReducer, {});
    return parts.timeZoneName;
};

/** User-friendly minimal string saying how long ago(?) this timestamp was.
 * 
 * @param timeObj {Date} object
 * @returns String
 */
export const asRelativeDateTime = (timeObj, fudge) => {
    const rtf = new Intl.RelativeTimeFormat(); // Let browser attempt best guess at locale and preferences
    timeObj = validateDateTime(timeObj);

    let seconds = parseInt((timeObj - new Date()) / 1000);
    if (fudge) {
        // V1 (PHP) Meetings may have a created_at in the future :-/
        // To work around this, fudge any settings value less than a day old as 'recently'
        if (seconds > -86400) {
            return 'Recently';
        }
    }

    // console.log("asRelativeDateTime: ", seconds);
    let secondsAbs = Math.abs(seconds);

    if (secondsAbs < 60) { // ~ Less than a minute
        return rtf.format(seconds, "second");
    } else if (secondsAbs < 3600) { // 60*60 ~ Less than an hour
        return rtf.format(parseInt(seconds/60), "minute");
    } else if (secondsAbs < 86400) { // 60*60*24 ~ Less than a day
        return rtf.format(parseInt(seconds/3600), "hour");
    } else if (secondsAbs < 604800) { // 60*60*24*7 ~ Less than a week
        return rtf.format(parseInt(seconds/86400), "day");
    } else if (secondsAbs < 3024000) { // 60*60*24*7*5 ~ Less than a month (5 weeks to be sure)
        return rtf.format(parseInt(seconds/604800), "week");
    } else {
        return rtf.format(parseInt(seconds/2592000), "month"); // 60*60*24*30 ~ Seconds in 30 days
    }

};


/** VERY naive function to get the 'date' ignoring the Timezone
 * DO NOT USE THIS: It breaks in other timezones :-(
 * Use /utils/Sort.js dateToLocalISO() instead.
 * 
 * This only exists now for 'Known bad' testing scenarios!
 */
export const asNaiveDateISO = (dateObj) => {
    return dateObj.toISOString().slice(0,10);
}


/** Given a duration in milliseconds, calculate the percentage between the two Date objects passed in.
 *
 * @param startTimeObj {Date} object
 * @param endTimeObj {Date} object
 * @param duration {Number} in milliseconds
 * @param minimum {Number} Minimum percentage to return
 * @returns {number|*}
 */
export const percentageWidthOfRange = (startTimeObj, endTimeObj, duration, minimum=0) => {
    const ms = endTimeObj - startTimeObj;
    //const mins = Math.floor(ms / (60*1000));
    const percentage = Math.floor((ms/duration) * 100);
    if (percentage < minimum) {
        return minimum;
    } else {
        return percentage;
    }
};


// https://bobbyhadz.com/blog/javascript-initialize-date-with-timezone
/*
export const changeTimeZone = (date, timeZone) => {
    if (typeof date === 'string') {
      return new Date(
        new Date(date).toLocaleString('en-US', {
          timeZone,
        }),
      );
    }
  
    return new Date(
      date.toLocaleString('en-US', {
        timeZone,
      }),
    );
  }
*/

// https://stackoverflow.com/questions/20712419/get-utc-offset-from-timezone-in-javascript
/*
export const getOffset = (timeZone) => {
    const timeZoneName = Intl.DateTimeFormat("ia", {
        timeZoneName: "short",
        timeZone,
    })
        .formatToParts()
        .find((i) => i.type === "timeZoneName").value;
    const offset = timeZoneName.slice(3);
    if (!offset) return 0;

    const matchData = offset.match(/([+-])(\d+)(?::(\d+))?/);
    if (!matchData) throw `cannot parse timezone name: ${timeZoneName}`;

    const [, sign, hour, minute] = matchData;
    let result = parseInt(hour) * 60;
    if (sign === "+") result *= -1;
    if (minute) result += parseInt(minute);

    return result;
 };
*/

 // https://stackoverflow.com/questions/36011227/javascript-check-if-time-ranges-overlap
 // De Morgans Laws https://en.wikipedia.org/wiki/De_Morgan%27s_laws
 export const hasOverlap = (a, b) => {
    // We assume a and b are Meetings / MeetingSlots / Appointments and both 
    // have startObj and endObj fields
    return (
        (a.startObj < b.endObj)
        &&
        (a.endObj > b.startObj)
    );
 };


/* Build the list of naive date strings for the event specified 
 * This is merged with the list of naive date strings that have Items in order to calc the list of valid event dates.
 * eventObj = { 
 *     ... 
 *     end: "2023-01-05",
 *     num_days: 2,
 *     start: "2023-01-04",
 *     timezone: "Pacific/Niue"
 * }
 * 
 * NOTE: This uses Date() objects without ANY timezone considerations!
 * The date is the date, no matter the timezone offset.
 * Whether a calendar item happens within that date is a consideration for later.
 */
export const calcEventDates = (eventObj) => {
    let out = [eventObj.start];

    if (eventObj.num_days == 2) {
        out.push(eventObj.end);
    } else if (eventObj.num_days > 2) {
        let curDate = new Date(eventObj.start);
        for (let x = 2; x <= eventObj.num_days; x++) {
            curDate.setDate(curDate.getDate()+1);
            out.push( curDate.toISOString().slice(0,10) );
        }
    }
    return out;
};



/* Return the SORTED UNION of the eventDates list and the itemDates list */
export const calcActiveDates = (eventDates, itemDates) => {
    return [...new Set([...eventDates, ...itemDates])].sort();
};


export const findPreviousDateKey = (activeDates, strDate) => {
    let i = activeDates.indexOf(strDate);
    // console.log("findPrev:", i);
    if (i > 0) { return activeDates[i - 1]; }

    // strDate doesn't have valid previous. 
    // Fallback check if see if we've fallen off the end of the list e.g. due to timezone switch.
    // Allow attendee to jump back to last valid day of the event.
    let last = activeDates[activeDates.length -1];
    if (last && (last < strDate)) { return last; }

    return null;
};
export const findNextDateKey = (activeDates, strDate) => {
    let i = activeDates.indexOf(strDate);
    // console.log("findNext:", activeDates, strDate, i);
    if ( 
        (i >= 0) &&
        (i < activeDates.length -1)
    ) { 
        // console.log("Returning: ", activeDates[i+1], i+1);
        return activeDates[i+1]; 
    }

    // strDate doesn't have valid next. 
    // Fallback check if see if we've fallen off the start of the list e.g. due to timezone switch.
    // Allow attendee to jump forward to first valid day of the event.
    if (activeDates[0] && (activeDates[0] > strDate)) { return activeDates[0]; }

    return null;
};


// Cache the local timezone for keyToLocalTime calculations
var _localTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
export const forceLocalTZ = (newTZ) => {
    _localTZ = newTZ;
};

/** Return a datetime object instantiated to the specified timezone using the SpaceTime.how library. */
/*
export const dateForTimezone = (dateStr) => {  //, timezone
    // let spacetimeObj = spacetime(dateStr, timezone);

    // let out = spacetimeObj.toNativeDate();
    // let noTZ = spacetimeObj.goto(null).toNativeDate();
    let localOnly = keyToLocalTime(dateStr);
    // console.log("dateForTimezone:", dateStr, timezone, out);
    // console.log("dateForTimezone:noTZ", noTZ);
    // console.log("dateForTimezone:localOnly", localOnly);
    return localOnly;
    // return spacetimeObj.toNativeDate();
};
*/

export const keyToLocalTime = (dateKey) => {
    // console.log("keyToLocalTimezone:", _localTZ);
    let spacetimeObj = spacetime(dateKey, _localTZ);
    return spacetimeObj.toNativeDate();
};

/*
export const keyToLocalTime = (dateKey, localTZ=null) => {
    if (!localTZ) {
        localTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    // const localTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log("keyToLocalTimezone:", localTZ);
    let spacetimeObj = spacetime(dateKey, localTZ);
    return spacetimeObj.toNativeDate();
};
*/

export const dayNameFromKey = (dateKey) => {  // , timezone
    return new Intl.DateTimeFormat(navigator.language, {weekday: 'long'}).format(keyToLocalTime(dateKey));
    // return new Intl.DateTimeFormat(navigator.language, {weekday: 'long'}).format(dateForTimezone(dateKey, timezone));
    // return new Intl.DateTimeFormat(navigator.language, {weekday: 'long'}).format(new Date(dateKey));
};


export const firstOfWeekObj = (dateKey) => {  // , timezone
    let first = keyToLocalTime(dateKey);
    // let first = dateForTimezone(dayInRange, timezone);
    // let first = new Date(dayInRange);
    first.setDate(first.getDate() - first.getDay());  // 0-6 0==Sunday
    // console.log("firstOfWeek:", dateKey, timezone, first);
    return first;
};

export const lastOfWeekObj = (dateKey) => {  // , timezone
    let first = keyToLocalTime(dateKey);
    // let first = dateForTimezone(dayInRange, timezone);
    // let first = new Date(dayInRange);
    first.setDate(first.getDate() + (6 - first.getDay()));  // 0-6 0==Sunday
    return first;
};

export const firstOfMonthObj = (dateKey) => {  // , timezone
    let first = keyToLocalTime(dateKey);
    // let first = dateForTimezone(dayInRange, timezone);
    // let first = new Date(dayInRange);
    first.setDate(1);
    return first;
};

export const lastOfMonthObj = (dateKey) => {  // , timezone
    let first = keyToLocalTime(dateKey);
    // let first = dateForTimezone(dayInRange, timezone);
    // let first = new Date(dayInRange);
    return new Date(
        first.getFullYear(),
        first.getMonth() +1, // Zeroth day of next month (months are 0-indexed)
        0
    );
};

